import { Link, useParams } from "react-router-dom";

import { LeadAvatar } from "@/components/alia/lead-avatar";
import { AllisonOrb } from "@/components/alia/allison-orb";
import { TagChip } from "@/components/alia/tag-chip";
import { cn, formatDebtCompact, relativeTime, STAGE_LABELS } from "@/lib/utils";
import type { Channel, Conversation, Stage } from "@/types";

const STAGE_RAIL: Record<Stage, string> = {
  nuevo: "bg-stage-nuevo",
  calificado: "bg-stage-calificado",
  propuesta: "bg-stage-propuesta",
  ganado: "bg-stage-ganado",
  perdido: "bg-stage-perdido",
};

const STAGE_CHIP: Record<Stage, string> = {
  nuevo: "text-stage-nuevo",
  calificado: "text-stage-calificado",
  propuesta: "text-stage-propuesta",
  ganado: "text-stage-ganado",
  perdido: "text-stage-perdido",
};

const CHANNEL_DOT: Record<Channel, string> = {
  web: "bg-channel-web",
  whatsapp: "bg-channel-whatsapp",
  instagram: "bg-channel-instagram",
  telegram: "bg-channel-telegram",
};

interface ConversationCardProps {
  conversation: Conversation;
}

export function ConversationCard({ conversation }: ConversationCardProps) {
  const { conversationId } = useParams();
  const isActive = conversationId === conversation.id;
  const { lead, preview, channel, status, unread, updatedAt } = conversation;

  return (
    <Link
      to={`/inbox/${conversation.id}`}
      className={cn(
        "group relative block overflow-hidden rounded-lg border transition-all",
        "border-border bg-surface hover:border-border-strong hover:bg-surface-2",
        isActive &&
          "!border-brand/40 !bg-brand/[0.04] ring-1 ring-brand/20"
      )}
    >
      {/* Stage rail */}
      <span
        aria-hidden
        className={cn(
          "absolute inset-y-0 left-0 w-[2px] transition-all",
          STAGE_RAIL[lead.stage],
          !isActive && "opacity-50 group-hover:opacity-100"
        )}
      />

      <div className="flex items-start gap-2.5 px-3 py-2.5 pl-4">
        <LeadAvatar
          name={lead.name}
          size={34}
          ring={unread > 0 ? "subtle" : "none"}
        />

        <div className="min-w-0 flex-1">
          {/* Top row: name · channel dot · status indicator (right) · time */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-1.5">
              <span
                aria-hidden
                className={cn(
                  "size-1.5 shrink-0 rounded-full",
                  CHANNEL_DOT[channel]
                )}
                title={channel}
              />
              <h3 className="truncate text-[13px] font-semibold leading-tight text-foreground">
                {lead.name}
              </h3>
              {unread > 0 && (
                <span className="inline-flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-brand px-1 text-[10px] font-semibold text-brand-foreground tabular-nums">
                  {unread}
                </span>
              )}
            </div>

            <div className="flex shrink-0 items-center gap-1.5">
              {/* Status mini-indicator (just the visual cue, no text) */}
              {status === "ai" && (
                <span title="Allison atendiendo">
                  <AllisonOrb size={11} active />
                </span>
              )}
              {status === "waiting" && (
                <span
                  className="size-1.5 animate-pulse rounded-full bg-warning"
                  title="Esperando respuesta"
                />
              )}
              {status === "human" && (
                <span
                  className="size-1.5 rounded-full bg-muted-foreground/60"
                  title="Atendido por humano"
                />
              )}
              {status === "resolved" && (
                <span
                  className="size-1.5 rounded-full bg-success"
                  title="Resuelto"
                />
              )}
              <time className="text-[10px] text-muted-foreground tabular-nums">
                {relativeTime(updatedAt)}
              </time>
            </div>
          </div>

          {/* Sub-row: stage · debt · city */}
          <div className="mt-0.5 flex items-center gap-1.5 text-[10.5px] text-muted-foreground">
            <span className={cn("font-medium", STAGE_CHIP[lead.stage])}>
              {STAGE_LABELS[lead.stage]}
            </span>
            {lead.debt && (
              <>
                <span className="text-border">·</span>
                <span className="font-medium tabular-nums text-foreground/85">
                  {formatDebtCompact(lead.debt)}
                </span>
              </>
            )}
            {lead.city && (
              <>
                <span className="text-border">·</span>
                <span className="truncate">{lead.city}</span>
              </>
            )}
          </div>

          {/* Preview */}
          <p className="mt-1.5 line-clamp-2 text-[12.5px] leading-snug text-muted-foreground group-hover:text-foreground/85">
            {preview}
          </p>

          {/* Tags inline (only show if any) */}
          {conversation.tags.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {conversation.tags.slice(0, 3).map((tag) => (
                <TagChip key={tag.id} tag={tag} size="sm" />
              ))}
              {conversation.tags.length > 3 && (
                <span className="inline-flex items-center px-1 text-[10px] text-muted-foreground">
                  +{conversation.tags.length - 3}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
